import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { inject, Injectable, signal } from "@angular/core";
import { EnvironmentService } from "./environment.service";
import { LogLevel, LogMessage } from "@husbanken/angular-starter";
import { HbError } from "../models/error.model";

@Injectable({ providedIn: "root" })
export class KlientloggerService {
  private readonly httpClient = inject(HttpClient);
  private readonly environmentService = inject(EnvironmentService);

  private readonly consoleLogLevel = this.environmentService.environment().logLevel;
  private readonly logMessages = signal<LogMessage[]>([]);

  constructor() {
    if (!this.environmentService.environment().klientloggerPath) {
      console.warn("Sti til klientlogger er ikke definert. Det er sannsynlig at klientlogging vil feile. Legg til 'klientloggerPath' i environment-objektet");
    }
  }

  private addLogEntry(logMessage: LogMessage): void {
    this.logMessages.update(logMessages => [...logMessages, logMessage]);

    this.httpClient.post(this.environmentService.environment().klientloggerPath, { lg: [logMessage] }).subscribe({
      error: error => console.error("Kall til klientloggeren feilet", error, logMessage),
    });
  }

  public debug(msg: string, ...args: any[]): void {
    if (this.consoleLogLevel <= LogLevel.DEBUG) {
      console.debug(msg, ...args);
    }
  }

  public info(msg: string, ...args: any[]): void {
    if (this.consoleLogLevel <= LogLevel.INFO) {
      console.info(msg, ...args);
    }

    this.addLogEntry({
      l: LogLevel.INFO,
      m: msg,
      t: Date.now(),
    });
  }

  public warn(msg: string, ...args: any[]): void {
    if (this.consoleLogLevel <= LogLevel.WARN) {
      console.warn(msg, ...args);
    }

    this.addLogEntry({
      l: LogLevel.WARN,
      m: msg,
      t: Date.now(),
    });
  }

  public error(error: HbError | HttpErrorResponse | string, ...args: any[]): void {
    if (this.consoleLogLevel <= LogLevel.ERROR) {
      console.error(error, ...args);
    }

    this.addLogEntry({
      l: LogLevel.ERROR,
      m: this.errorToString(error),
      t: Date.now(),
    });
  }

  private errorToString(error: HbError | HttpErrorResponse | string): string {
    if (!error) {
      return undefined;
    }

    if (typeof error == "string") {
      return `[raw=${error}]`;
    }

    return (
      `[name=${error?.name}` +
      `, message=${error?.message}` +
      `, path=${window?.location?.pathname}` +
      `\n${error?.["stack"]}` +
      `\ncaused by=${this.errorToString(error?.["cause"])}]`
    );
  }
}
